import { createClient } from '@supabase/supabase-js';

const SUPABASE_URL = process.env.REACT_APP_CRM_SUPABASE_URL;
const SUPABASE_KEY = process.env.REACT_APP_CRM_SUPABASE_KEY;
const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const supabase = (SUPABASE_URL && SUPABASE_KEY)
  ? createClient(SUPABASE_URL, SUPABASE_KEY)
  : null;

const vouchersApi = {
  // ─── Extração via IA ───
  async extractVouchers(files, context = '') {
    const formData = new FormData();
    if (context) formData.append('context', context);
    files.forEach(file => formData.append('file', file));
    const res = await fetch(`${API_URL}/vouchers/extract`, {
      method: 'POST',
      body: formData
    });
    return res.json();
  },

  async refineExtraction(currentData, correction) {
    const res = await fetch(`${API_URL}/vouchers/refine`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ current_data: currentData, correction })
    });
    return res.json();
  },

  // fileMap: { originalFilename: storagePath } — ex: { "Carro.pdf": "99ca98.../1234_Carro.pdf" }
  async saveExtractedItems(packageId, extractionResult, fileMap = {}) {
    if (!supabase || !extractionResult) return false;
    const items = extractionResult.timeline_items || [];
    const passengers = extractionResult.passengers || [];

    // Busca passageiros já existentes para não duplicar
    const { data: existingPax } = await supabase.from('travel_passengers')
      .select('full_name').eq('package_id', packageId);
    const existingNames = new Set((existingPax || []).map(p => p.full_name?.toUpperCase()));

    for (const pax of passengers) {
      if (existingNames.has((pax.full_name || '').toUpperCase())) continue;
      const names = (pax.full_name || '').split(' ');
      const initials = names.length >= 2
        ? (names[0][0] + names[names.length - 1][0]).toUpperCase()
        : (names[0] || '?').slice(0, 2).toUpperCase();
      await supabase.from('travel_passengers').insert({
        package_id: packageId, full_name: pax.full_name,
        short_name: names[0], initials,
        document_number: pax.document_number || null,
        is_primary: passengers.indexOf(pax) === 0 && existingNames.size === 0,
      });
    }

    // Busca maior sort_order existente
    const { data: existingItems } = await supabase.from('travel_timeline')
      .select('sort_order').eq('package_id', packageId).order('sort_order', { ascending: false }).limit(1);
    const maxOrder = existingItems?.[0]?.sort_order || 0;

    for (let i = 0; i < items.length; i++) {
      const item = items[i];
      // Resolve document_files: pega o storagePath do arquivo de origem
      const sourceFile = item.source_filename;
      const storagePath = sourceFile ? fileMap[sourceFile] : null;
      const documentFiles = storagePath ? [storagePath] : null;

      await supabase.from('travel_timeline').insert({
        package_id: packageId, item_type: item.item_type,
        icon: item.icon, color: item.color, title: item.title,
        subtitle: item.subtitle, date_label: item.date_label,
        start_datetime: item.start_datetime || null,
        end_datetime: item.end_datetime || null,
        sort_order: maxOrder + (item.sort_order || i + 1),
        details: item.details || {},
        document_files: documentFiles,
      });
    }

    const updates = {};
    if (extractionResult.package_title) updates.title = extractionResult.package_title;
    if (extractionResult.package_subtitle) updates.subtitle = extractionResult.package_subtitle;
    if (extractionResult.start_date) updates.start_date = extractionResult.start_date;
    if (extractionResult.end_date) updates.end_date = extractionResult.end_date;
    if (extractionResult.destinations) updates.destinations = extractionResult.destinations;
    if (Object.keys(updates).length > 0) {
      updates.updated_at = new Date().toISOString();
      await supabase.from('travel_packages').update(updates).eq('id', packageId);
    }
    return true;
  },

  // ─── Contatos do CRM ───
  async searchContacts(query) {
    if (!supabase || !query || query.length < 2) return [];
    const { data, error } = await supabase
      .from('contacts')
      .select('id, name, phone, email, cpf, celular, cidade, uf, nascimento, sexo, avatar_url, display_name')
      .or(`name.ilike.%${query}%,phone.ilike.%${query}%,display_name.ilike.%${query}%,email.ilike.%${query}%`)
      .order('name').limit(10);
    if (error) return [];
    return data || [];
  },

  async updateContact(id, updates) {
    if (!supabase) return null;
    const { data, error } = await supabase.from('contacts')
      .update({ ...updates, updated_at: new Date().toISOString() })
      .eq('id', id).select().single();
    if (error) return null;
    return data;
  },

  // ─── Travelers ───
  async findTravelerByContact(contactId) {
    if (!supabase) return null;
    const { data } = await supabase.from('travelers').select('*').eq('contact_id', contactId).maybeSingle();
    return data;
  },

  async createTraveler({ contact_id, full_name, email, phone, cpf }) {
    if (!supabase) return null;
    const { data, error } = await supabase.from('travelers')
      .insert({ contact_id, full_name, email, phone, cpf }).select().single();
    if (error) return null;
    return data;
  },

  // ─── Pacotes ───
  async getPackages() {
    if (!supabase) return [];
    const { data, error } = await supabase.from('travel_packages')
      .select(`*, travel_passengers(*), travel_documents(*), travel_timeline(*), travel_checklist(*),
        traveler:traveler_id(id, full_name, email, phone, cpf),
        contact:customer_id(id, name, phone, email, cpf, celular, cidade, uf, avatar_url)`)
      .order('created_at', { ascending: false });
    if (error) return [];
    return data || [];
  },

  async createPackage({ title, subtitle, customer_id, traveler_id, quote_id, start_date, end_date, destinations, country_flags }) {
    if (!supabase) return null;
    const code = await this._generateCode();
    const { data, error } = await supabase.from('travel_packages')
      .insert({ access_code: code, title, subtitle, customer_id: customer_id || null,
        traveler_id: traveler_id || null, quote_id: quote_id || null,
        start_date: start_date || null,
        end_date: end_date || null, destinations: destinations || [],
        country_flags: country_flags || '', status: 'draft' })
      .select().single();
    if (error) return null;
    return data;
  },

  async updatePackageStatus(id, status) {
    if (!supabase) return null;
    const { data, error } = await supabase.from('travel_packages')
      .update({ status, updated_at: new Date().toISOString() }).eq('id', id).select().single();
    if (error) return null;
    return data;
  },

  async publishRoteiro(packageId) {
    const res = await fetch(`${API_URL}/roteiro/publicar`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ package_id: packageId })
    });
    return res.json();
  },

  // ─── Documentos ───
  async uploadDocument(packageId, file, documentType) {
    if (!supabase) return null;
    const fileName = `${packageId}/${Date.now()}_${file.name}`;
    const { error: se } = await supabase.storage.from('travel-documents').upload(fileName, file);
    if (se) return null;
    const fileType = file.type.includes('pdf') ? 'pdf' : file.type.includes('image') ? 'image' : 'other';
    const { data, error } = await supabase.from('travel_documents')
      .insert({ package_id: packageId, file_path: fileName, file_type: fileType,
        file_name: file.name, document_type: documentType || null,
        extraction_status: 'pending' })
      .select().single();
    if (error) return null;
    // Retorna doc com file_path para vincular ao timeline_item via document_files
    return { ...data, file_path: fileName };
  },

  async updateDocExtractionStatus(docIds, status) {
    if (!supabase || !docIds.length) return;
    for (const id of docIds) {
      await supabase.from('travel_documents').update({ extraction_status: status }).eq('id', id);
    }
  },

  async deleteDocument(id, filePath) {
    if (!supabase) return false;
    if (filePath) await supabase.storage.from('travel-documents').remove([filePath]);
    const { error } = await supabase.from('travel_documents').delete().eq('id', id);
    return !error;
  },

  async deleteTimelineItem(id) {
    if (!supabase) return false;
    const { error } = await supabase.from('travel_timeline').delete().eq('id', id);
    return !error;
  },

  // ─── Helpers ───
  async _generateCode() {
    const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
    let code = '';
    for (let i = 0; i < 6; i++) code += chars[Math.floor(Math.random() * chars.length)];
    if (supabase) {
      const { data } = await supabase.from('travel_packages').select('id').eq('access_code', code).maybeSingle();
      if (data) return this._generateCode();
    }
    return code;
  },

  // ─── Busca pacote existente vinculado a uma cotação Won ───
  async getPackageByQuoteId(quoteId) {
    if (!supabase || !quoteId) return null;
    const { data, error } = await supabase
      .from('travel_packages')
      .select('id, title, status')
      .eq('quote_id', quoteId)
      .maybeSingle();
    if (error) { console.error('getPackageByQuoteId:', error); return null; }
    return data;
  },

  // ─── Busca contato por ID (para hidratar contato vindo da URL) ───
  async getContactById(id) {
    if (!supabase || !id) return null;
    const { data, error } = await supabase
      .from('contacts')
      .select('id, name, phone, email, cpf, celular, cidade, uf, nascimento, sexo, avatar_url, display_name')
      .eq('id', id)
      .single();
    if (error) { console.error('getContactById:', error); return null; }
    return data;
  },

  // ─── Itens vendidos da oportunidade ganha ───────────────────────────
  //
  // A venda pode ter MAIS DE UM item: uma alternativa por destino
  // selecionado. chosen_option_id é "a alternativa selecionada daquele
  // destino", nunca "a vencedora da oportunidade".
  //
  // Retorna { order_id, items[], total } ou null.
  async getSoldItems(contact_id) {
    try {
      const res = await fetch(`${API_URL}/pipeline/contacts/${contact_id}/quotes`);
      if (!res.ok) return null;
      const oportunidades = await res.json();

      const ganha = oportunidades.find(o => o.order_status === 'won');
      if (!ganha) return null;

      const items = [];
      for (const destino of ganha.destinos || []) {
        const sel = (destino.alternativas || []).find(a => a.is_selected);
        if (sel) items.push({ ...sel, destination: destino.destination });
      }

      // Legado: oportunidade ganha sem seleção registrada (registros
      // anteriores à seleção por destino). Cai para pipeline_status.
      if (items.length === 0) {
        for (const destino of ganha.destinos || []) {
          for (const alt of destino.alternativas || []) {
            if (alt.pipeline_status === 'won') {
              items.push({ ...alt, destination: destino.destination, legacy: true });
            }
          }
        }
      }

      if (items.length === 0) return null;

      return {
        order_id: ganha.order_id,
        items,
        total: ganha.selected_total || items.reduce(
          (s, i) => s + parseFloat(i.total_price || 0) + parseFloat(i.markup || 0), 0
        ),
      };
    } catch (e) {
      console.error('getSoldItems:', e);
      return null;
    }
  },

  // Compatibilidade: consumidores que ainda esperam UMA cotação.
  // Devolve o primeiro item vendido e sinaliza quando há mais de um.
  async getWonQuote(contact_id) {
    const venda = await this.getSoldItems(contact_id);
    if (!venda) return null;
    const primeiro = venda.items[0];
    return {
      quote_id: primeiro.id,
      ...primeiro,
      sale_total: venda.total,
      sale_item_count: venda.items.length,
      multi_item: venda.items.length > 1,
    };
  },
};

export default vouchersApi;
